import React from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  Divider,
  Grid,
  Box,
  Fade,
  Zoom,
  IconButton,
} from "@mui/material";
import MDTypography from "shared/components/MDTypography";
import MDButton from "shared/components/MDButton";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PrintIcon from "@mui/icons-material/Print";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import CreditCardIcon from "@mui/icons-material/CreditCard";

const ConfirmacionCierreDialog = ({
  open,
  onClose,
  formData,
  responseData,
  isAdmin,
  isExplotador,
  isOperador,
  formatCurrency,
  getSelectedUserName,
  getConfirmationData,
  colors,
  turnoCerrado = false,
  pdfGenerated = false,
  pdfUrl = null,
  pdfSavedOnServer = false,
  pdfServerMessage = "",
}) => {
  if (!formData && !responseData) {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            overflow: "hidden",
          },
        }}
      >
        <DialogContent>
          <Box textAlign="center" py={4}>
            <MDTypography variant="body1" mt={2}>
              Cargando datos del cierre...
            </MDTypography>
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  // Datos del formulario y respuesta
  const efectivoInicial = formData?.efectivoInicial || 0;
  const recaudoEfectivoReportado = formData?.recaudoEfectivo || 0;

  // Datos del sistema desde la respuesta
  const sistemaData = responseData?.data || {};
  const balanceData = sistemaData.balance?.[0] || {};
  const transData = sistemaData.trans || {};

  // Datos del sistema
  const efectivoSistema = transData.valorE || 0;
  const tarjetaSistema = transData.valorT || 0;
  const valorTotalSistema = efectivoSistema + tarjetaSistema;
  const diferenciaEfectivo = recaudoEfectivoReportado - efectivoSistema;

  const formatDateWithColombiaTime = (dateString) => {
    if (!dateString || dateString === "N/A") return "No disponible";
    try {
      const fecha = new Date(dateString);
      if (isNaN(fecha.getTime())) return "Fecha inválida";
      const fechaColombia = new Date(fecha.getTime() + 5 * 60 * 60 * 1000);
      return (
        fechaColombia.toLocaleDateString("es-CO") +
        " " +
        fechaColombia.toLocaleTimeString("es-CO", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        })
      );
    } catch (error) {
      console.warn("Error formateando fecha con ajuste Colombia:", error);
      return "Fecha inválida";
    }
  };

  const formatDateRegular = (dateString) => {
    if (!dateString || dateString === "N/A") return "No disponible";
    try {
      const fecha = new Date(dateString);
      if (isNaN(fecha.getTime())) return "Fecha inválida";
      return (
        fecha.toLocaleDateString("es-CO") +
        " " +
        fecha.toLocaleTimeString("es-CO", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        })
      );
    } catch (error) {
      console.warn("Error formateando fecha regular:", error);
      return "Fecha inválida";
    }
  };

  const getHoraActual = () => {
    const ahora = new Date();
    return ahora.toLocaleTimeString("es-CO", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });
  };

  const handleDownloadPDF = () => {
    if (pdfUrl) {
      const link = document.createElement("a");
      link.href = pdfUrl;
      link.download = `cierre_turno_${formData?.turnoId || "turno"}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="print-dialog-title"
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          border: `2px solid ${colors.success}`,
          overflow: "hidden",
          boxShadow: 24,
          position: "relative",
        },
      }}
    >
      <DialogTitle
        id="print-dialog-title"
        sx={{
          backgroundColor: colors.success,
          color: "white",
          borderBottom: `1px solid ${colors.border}`,
          py: 3,
          pr: 2,
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1.5}>
            <CheckCircleIcon sx={{ fontSize: 28 }} />
            <Box>
              <MDTypography variant="h6" fontWeight="bold" color="white">
                Comprobante de Cierre Generado
              </MDTypography>
              <MDTypography variant="caption" sx={{ opacity: 0.9 }}>
                {getHoraActual()}
              </MDTypography>
            </Box>
          </Box>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              color: "white",
              "&:hover": {
                backgroundColor: "rgba(255,255,255,0.1)",
              },
            }}
            aria-label="cerrar"
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ py: 3 }}>
        <DialogContentText component="div">
          <Fade in={turnoCerrado}>
            <Box
              sx={{
                mb: 3,
                p: 2,
                backgroundColor: colors.background,
                borderRadius: 1,
                borderLeft: `4px solid ${colors.success}`,
              }}
            >
              <Box display="flex" alignItems="center" gap={1.5}>
                <CheckCircleIcon sx={{ color: colors.success, fontSize: 24 }} />
                <Box>
                  <MDTypography variant="body1" fontWeight="bold" color={colors.success}>
                    Turno cerrado correctamente
                  </MDTypography>
                  <MDTypography variant="body2" color={colors.textSecondary}>
                    El turno ha sido registrado exitosamente en el sistema.
                    {pdfSavedOnServer && " El comprobante PDF se ha guardado en el servidor."}
                  </MDTypography>
                  {pdfServerMessage && (
                    <MDTypography
                      variant="caption"
                      color={colors.warning}
                      sx={{ mt: 0.5, display: "block" }}
                    >
                      ⚠️ {pdfServerMessage}
                    </MDTypography>
                  )}
                </Box>
              </Box>
            </Box>
          </Fade>

          <Paper
            variant="outlined"
            sx={{
              p: 3,
              backgroundColor: "white",
              borderColor: colors.border,
              fontFamily: "'Courier New', monospace",
            }}
          >
            <Box textAlign="center" mb={2}>
              <MDTypography variant="h6" fontWeight="bold" color={colors.primary} gutterBottom>
                CENTRO COMERCIAL ISERRA 100
              </MDTypography>
              <MDTypography variant="caption" color={colors.textSecondary}>
                NIT: 830.019.348-2 | SISTEMA: LINCE V7.0
              </MDTypography>
              <br />
              <MDTypography variant="caption" color={colors.textSecondary}>
                #ID MAQUINA: ISERRA 100 | ESTACIÓN: Caja {balanceData.NumeroCaja || "04"}
              </MDTypography>
              <br />
              <MDTypography variant="caption" color={colors.textSecondary}>
                COMPROBANTE DE CIERRE DE TURNO
              </MDTypography>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Grid container spacing={1} mb={2}>
              <Grid item xs={6}>
                <MDTypography variant="caption" color={colors.textSecondary}>
                  Fecha Generación:
                </MDTypography>
              </Grid>
              <Grid item xs={6} textAlign="right">
                <MDTypography variant="caption" fontWeight="bold">
                  {formatDateRegular(new Date().toISOString())}
                </MDTypography>
              </Grid>

              <Grid item xs={6}>
                <MDTypography variant="caption" color={colors.textSecondary}>
                  Usuario Operador:
                </MDTypography>
              </Grid>
              <Grid item xs={6} textAlign="right">
                <MDTypography variant="caption" fontWeight="bold">
                  {sistemaData.user_kme || "Explotador"}
                </MDTypography>
              </Grid>

              <Grid item xs={6}>
                <MDTypography variant="caption" color={colors.textSecondary}>
                  Estación:
                </MDTypography>
              </Grid>
              <Grid item xs={6} textAlign="right">
                <MDTypography variant="caption" fontWeight="bold">
                  Caja {balanceData.NumeroCaja || "04"}
                </MDTypography>
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />

            <Grid container spacing={1} mb={2}>
              <Grid item xs={6}>
                <MDTypography variant="caption" color={colors.textSecondary}>
                  Inicio de Turno:
                </MDTypography>
              </Grid>
              <Grid item xs={6} textAlign="right">
                <MDTypography variant="caption" fontWeight="bold">
                  {formatDateWithColombiaTime(balanceData.FHInicio || formData?.inicioTurno)}
                </MDTypography>
              </Grid>

              <Grid item xs={6}>
                <MDTypography variant="caption" color={colors.textSecondary}>
                  Fin de Turno:
                </MDTypography>
              </Grid>
              <Grid item xs={6} textAlign="right">
                <MDTypography variant="caption" fontWeight="bold">
                  {formatDateWithColombiaTime(balanceData.FHFin || formData?.fechaCierre)}
                </MDTypography>
              </Grid>

              <Grid item xs={6}>
                <MDTypography variant="caption" color={colors.textSecondary}>
                  Duración:
                </MDTypography>
              </Grid>
              <Grid item xs={6} textAlign="right">
                <MDTypography variant="caption" fontWeight="bold">
                  {(() => {
                    const inicio = balanceData.FHInicio || formData?.inicioTurno;
                    const fin = balanceData.FHFin || formData?.fechaCierre;
                    if (!inicio || !fin || inicio === "N/A" || fin === "N/A") return "N/A";
                    try {
                      const fechaInicio = new Date(inicio);
                      const fechaFin = new Date(fin);
                      if (isNaN(fechaInicio.getTime()) || isNaN(fechaFin.getTime())) return "N/A";
                      const diferenciaMs = fechaFin - fechaInicio;
                      const horas = Math.floor(diferenciaMs / (1000 * 60 * 60));
                      const minutos = Math.floor((diferenciaMs % (1000 * 60 * 60)) / (1000 * 60));
                      return `${horas}h ${minutos}m`;
                    } catch {
                      return "N/A";
                    }
                  })()}
                </MDTypography>
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />

            <Box mb={2}>
              <MDTypography variant="caption" fontWeight="bold" color={colors.primary} gutterBottom>
                RESUMEN FINANCIERO
              </MDTypography>

              <Grid container spacing={1} mb={1}>
                <Grid item xs={12}>
                  <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                    <AttachMoneyIcon sx={{ fontSize: 16, color: colors.success }} />
                    <MDTypography variant="caption" fontWeight="bold" color={colors.success}>
                      EFECTIVO
                    </MDTypography>
                  </Box>
                </Grid>

                <Grid item xs={6}>
                  <MDTypography variant="caption" color={colors.textSecondary}>
                    Efectivo Inicial:
                  </MDTypography>
                </Grid>
                <Grid item xs={6} textAlign="right">
                  <MDTypography variant="caption" fontWeight="bold">
                    {formatCurrency(efectivoInicial)}
                  </MDTypography>
                </Grid>

                <Grid item xs={6}>
                  <MDTypography variant="caption" color={colors.textSecondary}>
                    Recaudo Efectivo Reportado:
                  </MDTypography>
                </Grid>
                <Grid item xs={6} textAlign="right">
                  <MDTypography variant="caption" fontWeight="bold">
                    {formatCurrency(recaudoEfectivoReportado)}
                  </MDTypography>
                </Grid>

                <Grid item xs={6}>
                  <MDTypography variant="caption" color={colors.textSecondary}>
                    Efectivo Sistema:
                  </MDTypography>
                </Grid>
                <Grid item xs={6} textAlign="right">
                  <MDTypography variant="caption" fontWeight="bold">
                    {formatCurrency(efectivoSistema)}
                  </MDTypography>
                </Grid>

                <Grid item xs={6}>
                  <MDTypography variant="caption" color={colors.textSecondary} fontWeight="bold">
                    Diferencia:
                  </MDTypography>
                </Grid>
                <Grid item xs={6} textAlign="right">
                  <MDTypography
                    variant="caption"
                    fontWeight="bold"
                    color={diferenciaEfectivo >= 0 ? colors.success : colors.error}
                  >
                    {formatCurrency(diferenciaEfectivo)}
                  </MDTypography>
                </Grid>
              </Grid>

              <Grid container spacing={1} mb={1}>
                <Grid item xs={12}>
                  <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                    <CreditCardIcon sx={{ fontSize: 16, color: colors.info }} />
                    <MDTypography variant="caption" fontWeight="bold" color={colors.info}>
                      TARJETA
                    </MDTypography>
                  </Box>
                </Grid>

                <Grid item xs={6}>
                  <MDTypography variant="caption" color={colors.textSecondary}>
                    Recaudo Tarjeta Sistema:
                  </MDTypography>
                </Grid>
                <Grid item xs={6} textAlign="right">
                  <MDTypography variant="caption" fontWeight="bold">
                    {formatCurrency(tarjetaSistema)}
                  </MDTypography>
                </Grid>
              </Grid>

              <Grid container spacing={1} mb={1}>
                <Grid item xs={12}>
                  <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                    <Box sx={{ width: 16, height: 16, bgcolor: colors.primary, borderRadius: 1 }} />
                    <MDTypography variant="caption" fontWeight="bold" color={colors.primary}>
                      TOTAL SISTEMA
                    </MDTypography>
                  </Box>
                </Grid>

                <Grid item xs={6}>
                  <MDTypography variant="caption" color={colors.textSecondary}>
                    Efectivo + Tarjeta:
                  </MDTypography>
                </Grid>
                <Grid item xs={6} textAlign="right">
                  <MDTypography variant="caption" fontWeight="bold" color={colors.primary}>
                    {formatCurrency(valorTotalSistema)}
                  </MDTypography>
                </Grid>
              </Grid>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box mt={3} pt={2} borderTop={`1px dashed ${colors.border}`}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Box textAlign="center">
                    <MDTypography variant="caption" color={colors.textSecondary}>
                      _________________________
                    </MDTypography>
                    <br />
                    <MDTypography variant="caption" color={colors.textSecondary}>
                      Firma Responsable
                    </MDTypography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box textAlign="center">
                    <MDTypography variant="caption" color={colors.textSecondary}>
                      _________________________
                    </MDTypography>
                    <br />
                    <MDTypography variant="caption" color={colors.textSecondary}>
                      Sello y Fecha
                    </MDTypography>
                  </Box>
                </Grid>
              </Grid>
            </Box>

            <Box mt={3} textAlign="center">
              <MDTypography variant="caption" color={colors.textSecondary}>
                LINCE V7.0 | Este documento es un comprobante oficial
              </MDTypography>
              <br />
              <MDTypography variant="caption" color={colors.textSecondary} fontStyle="italic">
                Generado automáticamente por el sistema de cierre de turnos
              </MDTypography>
            </Box>
          </Paper>
        </DialogContentText>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2.5,
          borderTop: `1px solid ${colors.border}`,
          backgroundColor: colors.background,
          justifyContent: "center",
          gap: 2,
        }}
      >
        {pdfUrl && (
          <MDButton
            variant="contained"
            color="success"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadPDF}
            sx={{
              backgroundColor: colors.success,
              minWidth: 150,
              "&:hover": {
                backgroundColor: colors.success,
                opacity: 0.9,
              },
            }}
          >
            Descargar PDF
          </MDButton>
        )}
        <MDButton
          onClick={onClose}
          variant="outlined"
          startIcon={<CloseIcon />}
          sx={{
            borderColor: colors.border,
            color: colors.textSecondary,
            minWidth: 150,
            "&:hover": {
              borderColor: colors.textSecondary,
              backgroundColor: colors.background,
            },
          }}
        >
          Cerrar
        </MDButton>
      </DialogActions>
    </Dialog>
  );
};

ConfirmacionCierreDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  formData: PropTypes.object,
  responseData: PropTypes.object,
  isAdmin: PropTypes.bool.isRequired,
  isExplotador: PropTypes.bool,
  isOperador: PropTypes.bool.isRequired,
  formatCurrency: PropTypes.func.isRequired,
  getSelectedUserName: PropTypes.func.isRequired,
  getConfirmationData: PropTypes.func.isRequired,
  colors: PropTypes.object.isRequired,
  turnoCerrado: PropTypes.bool,
  pdfGenerated: PropTypes.bool,
  pdfUrl: PropTypes.string,
  pdfSavedOnServer: PropTypes.bool,
  pdfServerMessage: PropTypes.string,
};

ConfirmacionCierreDialog.defaultProps = {
  formData: null,
  responseData: null,
  isExplotador: false,
  turnoCerrado: false,
  pdfGenerated: false,
  pdfUrl: null,
  pdfSavedOnServer: false,
  pdfServerMessage: "",
};

export default ConfirmacionCierreDialog;
